"use client"

import { useState } from "react"
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js"
import RequestLoader from "../Loader/RequestLoader"
import CancellationPolicy from "./CancellationPolicy"
import { stripeAPI, bookingAPI, healthAPI } from "../../config/api"

const Payment = ({ bookingData, onPaymentSuccess, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loader, setLoader] = useState(false)
  const [error, setError] = useState("")
  const [setConnectionStatus] = useState({
    backend: "untested",
    stripe: "untested",
  })

  // Initialize form data from bookingData
  const [firstName, setFirstName] = useState(bookingData?.customerName?.split(" ")[0] || "")
  const [lastName, setLastName] = useState(bookingData?.customerName?.split(" ").slice(1).join(" ") || "")
  const [email, setEmail] = useState(bookingData?.customerEmail || "")
  const [contactNumber, setContactNumber] = useState(bookingData?.customerPhone || "")
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [cardFirstName, setCardFirstName] = useState("")
  const [cardLastName, setCardLastName] = useState("")

  // Get the total amount from booking data
  const totalAmount = bookingData?.totalAmount || 0
  const eventName = bookingData?.eventType || "Event"
  const eventDate = bookingData?.eventDate || ""
  const guests = bookingData?.guests || 1

  // Test backend connection with detailed logging
  const testBackendConnection = async () => {
    try {
      console.log("🔍 Testing backend connection...")
      setConnectionStatus((prev) => ({ ...prev, backend: "testing" }))

      const data = await healthAPI.check()

      console.log("✅ Backend health check successful:", data)
      setConnectionStatus((prev) => ({ ...prev, backend: "success" }))
      setError("")
      return true
    } catch (error) {
      console.error("❌ Backend connection failed:", error)
      setConnectionStatus((prev) => ({ ...prev, backend: "failed" }))
      setError(`Backend connection error: ${error.message}`)
      return false
    }
  }

  // Test Stripe connection with detailed logging
  const testStripeConnection = async () => {
    try {
      console.log("🔍 Testing Stripe connection...")
      setConnectionStatus((prev) => ({ ...prev, stripe: "testing" }))

      const data = await stripeAPI.testStripe()

      console.log("✅ Stripe connection test successful:", data)
      setConnectionStatus((prev) => ({ ...prev, stripe: "success" }))
      setError("")
      return true
    } catch (error) {
      console.error("❌ Stripe connection test failed:", error)
      setConnectionStatus((prev) => ({ ...prev, stripe: "failed" }))
      setError(`Stripe configuration error: ${error.message}`)
      return false
    }
  }

  // Create Payment Intent with enhanced error handling
  const createPaymentIntent = async () => {
    try {
      console.log("🔄 Creating payment intent...")

      const requestData = {
        amount: totalAmount,
        currency: "usd",
        customerInfo: {
          name: `${firstName} ${lastName}`,
          email: email,
          phone: contactNumber,
        },
        bookingDetails: {
          eventType: eventName,
          eventDate: eventDate,
          guests: guests,
          selectedUpsellNames: bookingData?.selectedUpsellNames || "None",
          specialRequests: bookingData?.specialRequests || "None",
        },
      }

      console.log("📤 Sending payment intent request:", {
        amount: requestData.amount,
        customerEmail: requestData.customerInfo.email,
        eventType: requestData.bookingDetails.eventType,
      })

      const data = await stripeAPI.createPaymentIntent(requestData)

      console.log("✅ Payment intent response:", {
        success: data.success,
        paymentIntentId: data.paymentIntentId,
        amount: data.amount,
        currency: data.currency,
        status: data.status,
        hasClientSecret: !!data.clientSecret,
      })

      // Validate the response
      if (!data.success) {
        throw new Error("Payment intent creation failed")
      }

      if (!data.clientSecret) {
        throw new Error("Payment intent created but client secret is missing")
      }

      if (!data.paymentIntentId) {
        throw new Error("Payment intent created but ID is missing")
      }

      return data
    } catch (error) {
      console.error("❌ Error creating payment intent:", error)
      throw error
    }
  }

  // Confirm Payment on Backend
  const confirmPaymentOnBackend = async (paymentIntentId) => {
    try {
      console.log("🔄 Confirming payment on backend...")

      const data = await stripeAPI.confirmPayment({
        paymentIntentId: paymentIntentId,
      })

      console.log("✅ Payment confirmed on backend")
      return data
    } catch (error) {
      console.error("❌ Error confirming payment:", error)
      throw error
    }
  }

  // Save booking to backend
  const saveBookingToBackend = async (paymentDetails) => {
    try {
      console.log("🔄 Saving booking to backend...")

      const data = await bookingAPI.create({
        customerName: `${firstName} ${lastName}`,
        customerEmail: email,
        customerPhone: contactNumber,
        eventType: eventName,
        eventId: bookingData?.eventId || "",
        eventDate: eventDate,
        guests: guests,
        totalAmount: totalAmount,
        selectedUpsells: bookingData?.selectedUpsells || [],
        selectedUpsellNames: bookingData?.selectedUpsellNames || "None",
        specialRequests: bookingData?.specialRequests || "None",
        status: "confirmed",
        paymentStatus: "completed",
        stripePaymentId: paymentDetails.paymentIntentId,
        paymentMethod: "card",
      })

      console.log("✅ Booking saved to backend")
      return data.booking
    } catch (error) {
      console.error("❌ Error saving booking:", error)
      throw error
    }
  }

  // Send confirmation email using Web3Forms
  const sendConfirmationEmail = async (paymentDetails) => {
    try {
      console.log("📧 Sending confirmation email to customer:", email)

      const emailData = {
        access_key: "59c6a3f5-afcb-4e45-8934-614bd229327c",
        subject: `🌿 Leafwell Farm - Booking Confirmed: ${eventName}`,
        from_name: "Leafwell Farm",
        email: email,
        message: `Dear ${firstName} ${lastName},

🎉 Your booking for ${eventName} has been CONFIRMED!

📋 BOOKING DETAILS:
📅 Date: ${new Date(eventDate).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
👥 Guests: ${guests}
💰 Total Paid: $${totalAmount}
${bookingData?.selectedUpsellNames && bookingData.selectedUpsellNames !== "None" ? `🎁 Add-ons: ${bookingData.selectedUpsellNames}` : ""}

📍 LOCATION:
🏡 Leafwell Farm
📍 341 Swiss Hill Rd N, Bethel, NY 12720

✨ WHAT'S INCLUDED:
• Cold plunge & steam facilities
• Farm-to-table meals
• Healing treatments
• Biohacking tools
• Nature trails & meditation

📋 IMPORTANT:
• Check-in: 2:00 PM
• Arrive 15 minutes early
• Wear comfortable clothing
• Bring water bottle

We'll contact you 48 hours before your event with final details.

Thank you for choosing Leafwell Farm!

🌱 The Leafwell Farm Team
info@leafwellfarm.com | 347-370-2217

Payment ID: ${paymentDetails.paymentIntentId}
Booking Reference: LWF-${Date.now()}`,
      }

      // Use standard fetch for external API (Web3Forms)
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(emailData),
      })

      const result = await response.json()

      if (result.success) {
        console.log("✅ Customer email sent successfully")
        return true
      } else {
        console.error("❌ Email service failed:", result.message)
        return false
      }
    } catch (error) {
      console.error("❌ Error sending customer email:", error)
      return false
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (loader) {
      console.log("⚠️ Payment already in progress, ignoring duplicate submission")
      return
    }

    try {
      setLoader(true)
      setError("")

      console.log("🚀 Starting payment process...")
      console.log("🔑 Frontend Stripe Key:", process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.substring(0, 30) + "...")

      // Validation checks
      if (!stripe || !elements) {
        throw new Error("Payment system not ready. Please refresh the page and try again.")
      }

      if (!agreeTerms) {
        throw new Error("Please agree to the terms and conditions.")
      }

      if (!firstName || !lastName || !email || !contactNumber || !cardFirstName || !cardLastName) {
        throw new Error("Please fill in all required fields.")
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        throw new Error("Please enter a valid email address.")
      }

      // Test backend connection first
      console.log("🔍 Testing backend connection...")
      const backendConnected = await testBackendConnection()
      if (!backendConnected) {
        return
      }

      // Test Stripe connection
      console.log("🔍 Testing Stripe connection...")
      const stripeConnected = await testStripeConnection()
      if (!stripeConnected) {
        return
      }

      console.log("🔄 Creating payment intent...")

      // Step 1: Create Payment Intent on backend
      const paymentIntentData = await createPaymentIntent()
      const { clientSecret, paymentIntentId } = paymentIntentData

      console.log("✅ Payment intent created:", paymentIntentId)
      console.log("🔐 Client secret received:", clientSecret?.substring(0, 20) + "...")

      // Step 2: Get card element and validate
      const cardElement = elements.getElement(CardNumberElement)

      if (!cardElement) {
        throw new Error("Card element not found. Please refresh the page and try again.")
      }

      console.log("🔄 Confirming payment with Stripe...")

      // Step 3: Confirm payment with Stripe
      const confirmResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${cardFirstName} ${cardLastName}`,
            email: email,
            phone: contactNumber,
          },
        },
      })

      console.log("📊 Stripe confirmation result:", {
        error: !!confirmResult.error,
        paymentIntent: !!confirmResult.paymentIntent,
        status: confirmResult.paymentIntent?.status,
      })

      if (confirmResult.error) {
        console.error("❌ Stripe payment error:", confirmResult.error)

        // Handle specific Stripe errors
        let errorMessage = "Payment failed. Please try again."

        switch (confirmResult.error.code) {
          case "card_declined":
            errorMessage = "Your card was declined. Please try a different card or contact your bank."
            break
          case "expired_card":
            errorMessage = "Your card has expired. Please use a different card."
            break
          case "incorrect_cvc":
            errorMessage = "Your card's security code is incorrect."
            break
          case "insufficient_funds":
            errorMessage = "Your card has insufficient funds."
            break
          case "processing_error":
            errorMessage = "An error occurred while processing your card. Please try again."
            break
          case "authentication_required":
            errorMessage = "Your card requires authentication. Please try again or use a different card."
            break
          case "resource_missing":
            errorMessage = "Payment session expired or invalid. Please refresh the page and try again."
            break
          default:
            errorMessage = confirmResult.error.message || "Payment failed. Please try again."
        }

        throw new Error(errorMessage)
      }

      const { paymentIntent } = confirmResult

      if (!paymentIntent) {
        throw new Error("Payment confirmation failed - no payment intent returned.")
      }

      console.log("💳 Payment Intent Status:", paymentIntent.status)

      if (paymentIntent.status === "succeeded") {
        console.log("✅ Payment succeeded:", paymentIntent.id)

        // Step 4: Confirm payment on backend
        await confirmPaymentOnBackend(paymentIntent.id)

        // Step 5: Save booking to backend
        const savedBooking = await saveBookingToBackend({
          paymentIntentId: paymentIntent.id,
          paymentMethodId: paymentIntent.payment_method,
        })

        // Step 6: Send confirmation email
        const emailSent = await sendConfirmationEmail({
          paymentIntentId: paymentIntent.id,
          paymentMethodId: paymentIntent.payment_method,
        })

        console.log("🎉 Payment completed successfully!")

        if (onPaymentSuccess) {
          onPaymentSuccess({
            paymentIntent: paymentIntent,
            amount: totalAmount,
            booking: savedBooking,
            emailSent: emailSent,
          })
        }
      } else if (paymentIntent.status === "requires_action") {
        console.log("⚠️ Payment requires additional action")
        throw new Error("Your payment requires additional authentication. Please try again or use a different card.")
      } else {
        console.log("⚠️ Payment not completed, status:", paymentIntent.status)
        throw new Error(`Payment was not completed. Status: ${paymentIntent.status}. Please try again.`)
      }
    } catch (error) {
      console.error("❌ Payment error:", error)
      setError(error.message || "Payment failed. Please try again.")
    } finally {
      setLoader(false)
    }
  }


  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold">Complete Your Booking</h1>
        {onCancel && (
          <button onClick={onCancel} className="text-gray-600 hover:text-gray-800 transition duration-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

     

      {/* Booking Summary */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <h3 className="font-semibold text-green-900 mb-3">📋 Booking Summary</h3>
        <div className="space-y-2 text-sm text-green-800">
          <div className="flex justify-between">
            <span>Event:</span>
            <span className="font-medium">{eventName}</span>
          </div>
          <div className="flex justify-between">
            <span>Date:</span>
            <span className="font-medium">
              {eventDate
                ? new Date(eventDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "Not selected"}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Guests:</span>
            <span className="font-medium">{guests}</span>
          </div>
          {bookingData?.nights && bookingData?.eventId === "leafwell-nature-stay" && (
            <div className="flex justify-between">
              <span>Nights:</span>
              <span className="font-medium">{bookingData.nights} night{bookingData.nights > 1 ? "s" : ""}</span>
            </div>
          )}
          {bookingData?.selectedUpsellNames && bookingData.selectedUpsellNames !== "None" && (
            <div className="flex justify-between">
              <span>Add-ons:</span>
              <span className="font-medium text-xs">{bookingData.selectedUpsellNames}</span>
            </div>
          )}
          <div className="border-t border-green-200 pt-2 flex justify-between">
            <span className="font-bold">Total Amount:</span>
            <span className="font-bold text-lg">${totalAmount}</span>
          </div>
        </div>
      </div>

      {/* Payment Security Icons */}
      <div className="mt-4 mb-6 md:mb-8 p-3 md:p-4 rounded-lg bg-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-sm md:text-base font-normal text-black">Secure Payment Powered by Stripe</span>
        <div className="flex space-x-2 md:space-x-4 items-center">
          <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">VISA</div>
          <div className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">MC</div>
          <div className="bg-blue-800 text-white px-2 py-1 rounded text-xs font-bold">AMEX</div>
          <div className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold">STRIPE</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex">
            <svg
              className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-red-800 text-sm font-medium">Payment Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {/* Guest Details */}
        <h2 className="text-lg md:text-xl font-semibold">Guest Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <input
          type="email"
          placeholder="Email Address (confirmation sent here)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        <input
          type="text"
          placeholder="Contact Number"
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value)}
          required
          className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />

        {/* Payment Information */}
        <h2 className="text-lg md:text-xl font-bold">Payment Information</h2>
        <h3 className="text-base md:text-[18px] font-semibold">Name on Card</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <input
            type="text"
            placeholder="First Name on Card"
            value={cardFirstName}
            onChange={(e) => setCardFirstName(e.target.value)}
            required
            className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <input
            type="text"
            placeholder="Last Name on Card"
            value={cardLastName}
            onChange={(e) => setCardLastName(e.target.value)}
            required
            className="border p-2 md:p-3 lg:py-4 w-full rounded-lg text-sm md:text-base focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Card Details */}
        <h3 className="text-base md:text-[18px] font-semibold">Card Details</h3>
        <div className="space-y-3">
          <div className="border rounded-lg p-2 md:p-3 lg:py-4">
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                },
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="border rounded-lg p-2 md:p-3 lg:py-4">
              <CardExpiryElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                  },
                }}
              />
            </div>
            <div className="border rounded-lg p-2 md:p-3 lg:py-4">
              <CardCvcElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

       
        {/* Cancellation Policy */}
        <CancellationPolicy />

        {/* Terms Agreement */}
        <div className="flex items-start">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={() => setAgreeTerms(!agreeTerms)}
            required
            className="mr-2 mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          /> 
          <label className="text-sm md:text-base">
            I agree to the{" "}
            <a href="/terms-of-service" className="text-blue-600 hover:underline">
              Privacy Policy
            </a>{" "}
            and{" "}
            <a href="/terms-of-service" className="text-blue-600 hover:underline">
              Terms of Service
            </a>
          </label>
        </div>

        {/* Total Amount Display */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex justify-between items-center text-base md:text-lg font-semibold">
            <span>Total Amount:</span>
            <span className="text-green-600">${totalAmount}</span>
          </div>
          <p className="text-xs md:text-sm text-green-700 mt-1">Payment will be processed via Stripe</p>
        </div>

        {/* Submit Button */}
        <button
          disabled={loader || !agreeTerms}
          type="submit"
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 md:py-4 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
        >
          {loader ? <RequestLoader /> : `Pay $${totalAmount} - Complete Booking`}
        </button>

        {/* Cancel Button */}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full bg-gray-200 text-gray-800 py-3 md:py-4 rounded-xl font-semibold hover:bg-gray-300 transition duration-300 text-sm md:text-base"
          >
            Cancel
          </button>
        )}
      </form>
    </div>
  )
}

export default Payment
